const COMUNI_JSON_URL = 'https://raw.githubusercontent.com/matteocontrini/comuni-json/master/comuni.json';

function resolveApiBaseUrl() {
    if (typeof process !== 'undefined' && process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    if (typeof window !== 'undefined') {
        const { protocol, hostname, port } = window.location || {};
        if (protocol && hostname) {
            const portSuffix = port ? `:${port}` : (protocol === 'https:' ? '' : '');
            return `${protocol}//${hostname}${portSuffix}/api`;
        }
    }
    return null;
}

let comuniCache = null;
let comuniLoadPromise = null;

function normalizeComuneName(value) {
    return String(value || '')
        .trim()
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Z0-9\s']/g, ' ')
        .replace(/\s+/g, ' ');
}

async function loadComuniFallback() {
    if (comuniCache) return comuniCache;
    if (!comuniLoadPromise) {
        comuniLoadPromise = fetch(COMUNI_JSON_URL)
            .then((res) => {
                if (!res.ok) throw new Error('Comuni JSON load failed');
                return res.json();
            })
            .then((data) => {
                comuniCache = Array.isArray(data) ? data : [];
                return comuniCache;
            })
            .catch(() => {
                comuniCache = [];
                return comuniCache;
            });
    }
    return comuniLoadPromise;
}

function comuneCodice(c) {
    const catastale = String(c.codiceCatastale || '').toUpperCase();
    if (catastale.length === 4 && !/^\d/.test(catastale)) return catastale;
    const raw = String(c.codice || '').toUpperCase();
    if (raw.length === 4 && !/^\d/.test(raw)) return raw;
    return catastale.slice(0, 4);
}

function lookupInDataset(comuni, birthPlace, birthPlaceZip) {
    if (!comuni.length || !birthPlace) return null;

    const name = normalizeComuneName(birthPlace);
    const zip = String(birthPlaceZip || '').replace(/\D/g, '');
    const caps = zip.length === 5 ? [zip] : [];

    const matches = comuni.filter((c) => {
        const comuneName = normalizeComuneName(c.nome);
        if (comuneName !== name && !comuneName.startsWith(`${name} `)) return false;
        if (!caps.length) return true;
        const comuneCaps = Array.isArray(c.cap) ? c.cap.map((x) => String(x)) : [];
        return comuneCaps.some((cap) => caps.includes(cap));
    });

    if (matches.length === 0) {
        const byName = comuni.filter((c) => normalizeComuneName(c.nome) === name);
        if (byName.length === 1) return comuneCodice(byName[0]) || null;
        return null;
    }

    if (matches.length === 1) return comuneCodice(matches[0]) || null;

    if (caps.length) {
        const withCap = matches.find((c) => Array.isArray(c.cap) && c.cap.includes(caps[0]));
        if (withCap) return comuneCodice(withCap) || null;
    }

    return comuneCodice(matches[0]) || null;
}

/**
 * Risolve il codice catastale (4 lettere) da comune di nascita + CAP via API backend.
 */
export async function lookupBelfioreCode(birthPlace, birthPlaceZip, api = null) {
    const comune = String(birthPlace || '').trim();
    const cap = String(birthPlaceZip || '').replace(/\D/g, '');
    if (!comune) return null;

    if (api) {
        try {
            const res = await api.get('comuni_belfiore.php', {
                params: { comune, cap },
            });
            const codice = res.data?.codice;
            if (codice && String(codice).length === 4) {
                return String(codice).toUpperCase();
            }
        } catch {
            // fallback sotto
        }
    }

    const baseUrl = resolveApiBaseUrl();
    if (baseUrl) {
        try {
            const url = new URL('comuni_belfiore.php', baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`);
            url.searchParams.set('comune', comune);
            if (cap.length === 5) url.searchParams.set('cap', cap);
            const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
            const res = await fetch(url.toString(), {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (res.ok) {
                const data = await res.json();
                if (data?.codice) return String(data.codice).toUpperCase();
            }
        } catch {
            // fallback dataset
        }
    }

    const comuni = await loadComuniFallback();
    return lookupInDataset(comuni, birthPlace, birthPlaceZip);
}

export function canAttemptFiscalCodeGeneration({ fullName, birthDate, gender, birthPlace, birthPlaceZip }) {
    const zip = String(birthPlaceZip || '').replace(/\D/g, '');
    return Boolean(
        String(fullName || '').trim()
        && birthDate
        && gender
        && String(birthPlace || '').trim()
        && zip.length === 5,
    );
}
